import { useCallback, useEffect, useRef } from 'react'
import { Location, useBlocker } from 'react-router'
import { useConfirm } from '../useConfirm'

export const USE_NAVIGATE_CONFIRM_BYPASS_KEY = '__useNavigateConfirmBypass'

/**
 * Show a confirmation dialog while `enabled` is true.
 * Pass { state:{ __useNavigateConfirmBypass:true } } to navigate() to skip it.
 */
export function useNavigateConfirm(enabled: boolean, message: string) {
  const confirm = useConfirm()
  const dialogOpen = useRef(false) // only one dialog at a time
  const cancelledKey = useRef<string | null>(null) // key of a nav the user cancelled

  /* predicate: block when enabled, unless caller set the bypass flag */
  const shouldBlock = useCallback(
    ({ nextLocation }: { nextLocation: Location }) => {
      if (!enabled) return false

      /* Did we *just* cancel this exact transition?  Let it pass.  */
      if (nextLocation.key === cancelledKey.current) {
        cancelledKey.current = null
        return false
      }

      return !nextLocation.state?.[
        USE_NAVIGATE_CONFIRM_BYPASS_KEY as keyof typeof nextLocation.state
      ]
    },
    [enabled]
  )

  const blocker = useBlocker(shouldBlock)

  /* when the transition is blocked, ask the user once */
  useEffect(() => {
    if (
      blocker.state !== 'blocked' ||
      dialogOpen.current ||
      cancelledKey.current === blocker.location.key
    )
      return

    dialogOpen.current = true
    ;(async () => {
      const { data: ok } = await confirm(message)
      dialogOpen.current = false

      if (ok) {
        blocker.proceed?.() // continue to the target URL
      } else {
        /* remember the transition we’re cancelling */
        cancelledKey.current = blocker.location.key
        blocker.reset?.() // stay on the current page
      }
    })()
  }, [blocker, confirm, message])
}
